import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { RotateCcw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// App-level error boundary. A render error anywhere below (a page, the shell, a
// panel) is caught here and shown as a readable card instead of unmounting the
// whole React tree to a blank white page — the failure mode that hid the AppShell
// rules-of-hooks crash (React #300) behind a "blank until refresh" symptom.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced in the console + any telemetry hook; never swallowed silently.
    console.error('[ErrorBoundary] render error:', error, info.componentStack);
  }

  private handleReload = (): void => {
    this.setState({ error: null });
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="max-w-xl mx-auto px-8 py-16">
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Something went wrong rendering this page</MessageBarTitle>
            <span className="font-mono text-xs break-words">{error.message}</span>
          </MessageBarBody>
        </MessageBar>
        <div className="mt-4">
          <Button appearance="primary" icon={<RotateCcw size={16} strokeWidth={1.5} />} onClick={this.handleReload}>
            Reload
          </Button>
        </div>
      </div>
    );
  }
}
